import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import { ETATS_FACTURE } from '@/constants';
import type { RegieType } from '@/types';
import { getColorFromEtat } from '@/utils/facture';

import { SquareTag } from '../Autre';

interface EtatFactureSquareTagsProps {
  etat: string;
  regie?: RegieType;
  dateFacture: string;
  dateFactureProlongee: string;
  prelevee: boolean;
  dateValidationPosteComptable?: string;
  dateRejet?: string;
  dateDernierEncaissement?: string;
  dateDerniereRelance?: string;
  /**
   * Contrôle l'affichage des tags de prélèvements
   * - 'all' : affiche tous les tags (défaut)
   * - 'prelevement-only' : affiche uniquement les tags de prélèvements (P)
   * - 'no-prelevement' : masque tous les tags de prélèvements
   */
  prelevementFilter?: 'all' | 'prelevement-only' | 'no-prelevement';
}

const EtatFactureSquareTags = (props: EtatFactureSquareTagsProps) => {
  const {
    etat,
    regie,
    dateFacture,
    dateFactureProlongee,
    prelevee,
    dateValidationPosteComptable,
    dateRejet,
    dateDernierEncaissement,
    dateDerniereRelance,
    prelevementFilter = 'all',
  } = props;

  // Variables mémorisées pour optimiser les recalculs
  const today = React.useMemo(() => dayjs(), []);
  const dateLimiteEncaissement = React.useMemo(() => dayjs(dateFacture), [dateFacture]);
  const dateLimiteProlongee = React.useMemo(
    () => dayjs(dateFactureProlongee),
    [dateFactureProlongee],
  );

  const tagColor = React.useMemo(() => getColorFromEtat(etat), [etat]);

  // Vérifier en amont si le transfert est effectué
  // (pour éviter d'ajouter R si T doit être affiché)
  const transfertEffectue = React.useMemo(
    () =>
      (etat === ETATS_FACTURE.IMPAYE_PARTIEL || etat === ETATS_FACTURE.IMPAYE_TOTAL) &&
      !_.isNil(dateValidationPosteComptable),
    [etat, dateValidationPosteComptable],
  );

  // Vérifier quels tags afficher selon le filtre
  const shouldShowPrelevementTags = React.useMemo(
    () => prelevementFilter === 'all' || prelevementFilter === 'prelevement-only',
    [prelevementFilter],
  );

  const shouldShowOtherTags = React.useMemo(
    () => prelevementFilter === 'all' || prelevementFilter === 'no-prelevement',
    [prelevementFilter],
  );

  const shouldShowRelanceTag = React.useMemo(() => {
    // S'il n'y a pas de relance, on le mettra jamais
    if (_.isNil(dateDerniereRelance)) return false;

    // Si la facture prélèvement et non rejetée, pas de relance
    if (prelevee && _.isNil(dateRejet)) return false;

    // Si facture prélevée rejetée, afficher R seulement si relancée APRÈS le rejet
    if (
      prelevee &&
      !_.isNil(dateRejet) &&
      dayjs(dateDerniereRelance).isSameOrBefore(dayjs(dateRejet))
    ) {
      return false;
    }

    // Sinon si la facture a été totalement encaissée avant la dernière date de relance
    // On affiche pas le tag
    if (
      etat === ETATS_FACTURE.ENCAISSEE &&
      dayjs(dateDernierEncaissement).isBefore(dayjs(dateDerniereRelance))
    ) {
      return false;
    }

    // Sinon s'il n'y a pas d'encaissement (ou alors partiel) et qu'il y a une relance
    return true;
  }, [dateDerniereRelance, dateDernierEncaissement, etat, prelevee, dateRejet]);

  return (
    <>
      {prelevee &&
        shouldShowPrelevementTags &&
        (!_.isNil(dateRejet) ? (
          <SquareTag
            key="P-rejected"
            text="P"
            color={tagColor}
            title="Prélèvement rejeté"
            strikethrough
          />
        ) : (
          <SquareTag key="P" text="P" color={tagColor} title="Prélèvement" />
        ))}
      {shouldShowOtherTags &&
        (transfertEffectue ? (
          <SquareTag key="T" text="T" color={tagColor} title="Trésorerie" />
        ) : (
          shouldShowRelanceTag && (
            <>
              {/* Branche 1 :
           Pour les régies avec prolongation, entre dateFacture et dateFactureProlongee
          */}
              {regie?.regieProlongee &&
                today.isAfter(dateLimiteEncaissement) &&
                today.isSameOrBefore(dateLimiteProlongee) &&
                (etat === ETATS_FACTURE.A_ENCAISSER ||
                  etat === ETATS_FACTURE.ENCAISSEMENT_PARTIEL ||
                  etat === ETATS_FACTURE.ENCAISSEE) && (
                  <SquareTag key="R" text="R" color={tagColor} title="Relancé" />
                )}
              {/* Branche 2 : Après la date limite finale (prolongée ou non) */}
              {today.isAfter(
                regie?.regieProlongee ? dateLimiteProlongee : dateLimiteEncaissement,
              ) && (
                <>
                  {(etat === ETATS_FACTURE.IMPAYE_PARTIEL ||
                    etat === ETATS_FACTURE.IMPAYE_TOTAL ||
                    etat === ETATS_FACTURE.ENCAISSEMENT_PARTIEL) && (
                    <SquareTag key="R" text="R" color={tagColor} title="Relancé" />
                  )}
                  {etat === ETATS_FACTURE.ENCAISSEE && (
                    <SquareTag key="R" text="R" color={tagColor} title="Relancé" />
                  )}
                </>
              )}
            </>
          )
        ))}
    </>
  );
};

export default EtatFactureSquareTags;
